import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import type { OpsDocEntry } from "@shared/ops-docs";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { parseErrorMessage } from "@/lib/queryClient";
import { OPS_DOC_CATEGORY_LABEL } from "./constants";

export function OpsDocsPanel() {
  const { toast } = useToast();
  const [openDoc, setOpenDoc] = useState<OpsDocEntry | null>(null);

  const docsQuery = useQuery<OpsDocEntry[]>({
    queryKey: ["/api/admin/ops-docs"],
    queryFn: async () => {
      const res = await fetch("/api/admin/ops-docs", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load important links");
      return res.json();
    },
  });

  const contentQuery = useQuery<{ id: string; title: string; content: string }>({
    queryKey: ["/api/admin/ops-docs", openDoc?.id],
    enabled: !!openDoc && openDoc.location.kind === "doc",
    queryFn: async () => {
      const res = await fetch(`/api/admin/ops-docs/${encodeURIComponent(openDoc!.id)}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Failed to load doc content");
      }
      return res.json();
    },
  });

  useEffect(() => {
    if (contentQuery.error) {
      toast({
        title: "Couldn't load doc",
        description: parseErrorMessage(contentQuery.error, "Unknown error"),
        variant: "destructive",
      });
    }
  }, [contentQuery.error, toast]);

  const docs = docsQuery.data ?? [];

  return (
    <div className="space-y-4" data-testid="panel-important-links">
      <div>
        <h2 className="text-lg font-semibold">Important Links</h2>
        <p className="text-sm text-muted-foreground">
          Curated runbooks, playbooks, and dashboards for on-call and admins. Click View to read a runbook inline; external links open in a new tab.
        </p>
      </div>

      {docsQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : docsQuery.error ? (
        <p className="text-sm text-destructive" data-testid="text-ops-docs-error">
          Failed to load: {(docsQuery.error as Error)?.message ?? "Unknown error"}
        </p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No links registered yet.</p>
      ) : (
        <Table data-testid="table-important-links">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Category</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[280px]">Where it lives</TableHead>
              <TableHead className="w-[120px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((doc) => (
              <TableRow key={doc.id} data-testid={`row-ops-doc-${doc.id}`}>
                <TableCell>
                  <Badge variant="secondary" data-testid={`badge-ops-doc-category-${doc.id}`}>
                    {OPS_DOC_CATEGORY_LABEL[doc.category] ?? doc.category}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium" data-testid={`text-ops-doc-title-${doc.id}`}>
                  {doc.title}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`text-ops-doc-description-${doc.id}`}>
                  {doc.description}
                </TableCell>
                <TableCell className="text-xs font-mono break-all" data-testid={`text-ops-doc-location-${doc.id}`}>
                  {doc.location.kind === "doc" ? doc.location.path : doc.location.url}
                </TableCell>
                <TableCell className="text-right">
                  {doc.location.kind === "doc" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenDoc(doc)}
                      data-testid={`button-view-ops-doc-${doc.id}`}
                    >
                      <BookOpen className="mr-1 h-4 w-4" />
                      View
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      data-testid={`link-open-ops-doc-${doc.id}`}
                    >
                      <a href={doc.location.url} target="_blank" rel="noreferrer noopener">
                        <ExternalLink className="mr-1 h-4 w-4" />
                        Open
                      </a>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!openDoc} onOpenChange={(open) => { if (!open) setOpenDoc(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-ops-doc">
          <DialogHeader>
            <DialogTitle data-testid="text-ops-doc-dialog-title">{openDoc?.title}</DialogTitle>
            <DialogDescription>
              {openDoc?.location.kind === "doc" ? (
                <span className="font-mono text-xs">{openDoc.location.path}</span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {contentQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
            </div>
          ) : contentQuery.data ? (
            <article
              className="prose prose-sm dark:prose-invert max-w-none"
              data-testid="text-ops-doc-content"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentQuery.data.content}</ReactMarkdown>
            </article>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
